/**
 * Mietpark (rental) — models the legacy rental module (rental_orders/items/transports), but with
 * the gaps the legacy left open, adapted for FORQ:
 *  - real central billing (legacy was a dead-end flag): monthly + final settlement feed FORQ Finance;
 *  - tiered pricing (daily/weekly/monthly) + minimum duration + Kaution (legacy: daily-only);
 *  - real meter/condition/damage data on return (legacy: printed blanks);
 *  - a real, rights-checked transition graph (legacy set status freely);
 *  - two decoupled axes (operational status ⟂ billing status), like the invoice model.
 */
export type RentalStatus =
  | 'request' // Anfrage
  | 'reserved' // Reserviert
  | 'active' // Vermietet (nach Auslieferung)
  | 'return_scheduled' // Rückholung geplant
  | 'completed' // Abgeschlossen (zurückgenommen)
  | 'cancelled'; // Storniert

/** Billing axis — separate from the operational lifecycle. */
export type RentalBillingStatus = 'open' | 'ready' | 'billed';

export type RateType = 'daily' | 'weekly' | 'monthly';
export type TransportType = 'delivery' | 'pickup';
export type TransportStatus = 'open' | 'scheduled' | 'in_transit' | 'done';

export interface RentalItem {
  readonly id: string;
  readonly machineNumber: string;
  readonly machineLabel: string;
  readonly dailyRate: number;
  /** 0 = tier not offered. */
  readonly weeklyRate: number;
  readonly monthlyRate: number;
  readonly minimumDays: number;
  readonly startDate: string;
  readonly endDate: string;
  readonly actualReturnDate: string | null;
  readonly meterOut: number | null;
  readonly meterIn: number | null;
  readonly damageNote: string;
  readonly damageAmount: number;
}

export interface RentalTransport {
  readonly id: string;
  readonly number: string; // MT-26-NNNNNN
  readonly type: TransportType;
  readonly plannedDate: string;
  readonly driverName: string;
  readonly status: TransportStatus;
  readonly completedAt: string | null;
}

/** Standard = one continuous period; interval = a recurring weekday over a period (each occurrence
 *  is a separate delivery+pickup, only the used days are billed). */
export type RentalKind = 'standard' | 'interval';

export interface RentalOrder {
  readonly id: string;
  readonly tenantId: string;
  readonly number: string; // MI-26-NNNNNN
  readonly customerNumber: string;
  readonly customerName: string;
  readonly status: RentalStatus;
  readonly billingStatus: RentalBillingStatus;
  readonly kind: RentalKind;
  /** Interval only: weekday 0=So … 5=Fr … 6=Sa (JS getUTCDay). */
  readonly intervalWeekday: number | null;
  /** Cost per transport trip (interval bills 2 trips per occurrence). */
  readonly transportCost: number;
  readonly startDate: string;
  readonly endDate: string;
  readonly siteAddress: string;
  readonly contactName: string;
  readonly contactPhone: string;
  readonly deliveryRequired: boolean;
  readonly pickupRequired: boolean;
  readonly depositAmount: number;
  readonly note: string;
  readonly items: readonly RentalItem[];
  readonly transports: readonly RentalTransport[];
  /** Date up to which the rental has already been (monthly) billed; null = nothing billed yet. */
  readonly billedThrough: string | null;
  readonly createdAt: string;
}

// ---- status graph ------------------------------------------------------------

export const rentalTransitions: Readonly<Record<RentalStatus, readonly RentalStatus[]>> = {
  request: ['reserved', 'cancelled'],
  reserved: ['active', 'cancelled'],
  active: ['return_scheduled', 'completed'],
  return_scheduled: ['completed'],
  completed: [],
  cancelled: [],
};

export function allowedRentalTransitionsFor(status: RentalStatus): readonly RentalStatus[] {
  return rentalTransitions[status];
}

export const rentalStatusLabels: Readonly<Record<RentalStatus, string>> = {
  request: 'Anfrage',
  reserved: 'Reserviert',
  active: 'Vermietet',
  return_scheduled: 'Rückholung geplant',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
};

export const rentalActionLabels: Readonly<Record<RentalStatus, string>> = {
  request: 'Als Anfrage',
  reserved: 'Reservieren',
  active: 'Ausliefern',
  return_scheduled: 'Rückholung planen',
  completed: 'Rücknahme abschließen',
  cancelled: 'Stornieren',
};

/** Permission accepted to move a rental INTO the given status. */
export const rentalTransitionPermission: Readonly<Record<RentalStatus, string>> = {
  request: 'rental.edit',
  reserved: 'rental.edit',
  active: 'rental.dispatch',
  return_scheduled: 'rental.dispatch',
  completed: 'rental.return',
  cancelled: 'rental.edit',
};

export const billingStatusLabels: Readonly<Record<RentalBillingStatus, string>> = {
  open: 'Laufend',
  ready: 'Schlussabrechnung offen',
  billed: 'Abgerechnet',
};

export interface RentalTransition {
  readonly toStatus: RentalStatus;
  readonly label: string;
  readonly permission: string;
  readonly tone: 'default' | 'danger';
}

export function rentalFlowTransitions(order: RentalOrder, permissions: ReadonlySet<string>): readonly RentalTransition[] {
  return allowedRentalTransitionsFor(order.status)
    .map((toStatus) => ({
      toStatus,
      label: rentalActionLabels[toStatus],
      permission: rentalTransitionPermission[toStatus],
      tone: toStatus === 'cancelled' ? ('danger' as const) : ('default' as const),
    }))
    .filter((transition) => permissions.has(transition.permission));
}

// ---- dates / pricing / availability -----------------------------------------

/** Inclusive day count between two ISO dates (min 1). */
export function rentalDays(fromIso: string, toIso: string): number {
  const days = Math.round((Date.parse(toIso) - Date.parse(fromIso)) / 86_400_000) + 1;
  return Math.max(1, days);
}

/** Tiered charge for an item over [from, to]: full months, then full weeks, then days; honours minimumDays.
 *  Nimmt nur die Preisfelder, damit es auch mit einem noch nicht angelegten Satz (im Erfassen-Formular) rechnet. */
export function computeItemCharge(
  item: Pick<RentalItem, 'dailyRate' | 'weeklyRate' | 'monthlyRate' | 'minimumDays'>,
  fromIso: string,
  toIso: string,
): number {
  let remaining = Math.max(rentalDays(fromIso, toIso), item.minimumDays || 1);
  let charge = 0;
  if (item.monthlyRate > 0) {
    const months = Math.floor(remaining / 30);
    charge += months * item.monthlyRate;
    remaining -= months * 30;
  }
  if (item.weeklyRate > 0) {
    const weeks = Math.floor(remaining / 7);
    charge += weeks * item.weeklyRate;
    remaining -= weeks * 7;
  }
  charge += remaining * item.dailyRate;
  return Math.round(charge * 100) / 100;
}

/** Whether two closed date ranges overlap (inclusive). */
export function rangesOverlap(aFrom: string, aTo: string, bFrom: string, bTo: string): boolean {
  return aFrom <= bTo && bFrom <= aTo;
}

const blockingStatuses = new Set<RentalStatus>(['reserved', 'active', 'return_scheduled']);

export const weekdayLabels: readonly string[] = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

/** All dates in [from, to] falling on the given weekday (0=So … 6=Sa). */
export function intervalDates(fromIso: string, toIso: string, weekday: number): readonly string[] {
  const dates: string[] = [];
  const cursor = new Date(`${fromIso}T00:00:00.000Z`);
  const end = Date.parse(`${toIso}T00:00:00.000Z`);
  while (cursor.getTime() <= end) {
    if (cursor.getUTCDay() === weekday) dates.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}

/** Interval occurrences of an order that fall within [from, to] (used for period billing). */
export function occurrencesInRange(order: RentalOrder, fromIso: string, toIso: string): readonly string[] {
  if (order.kind !== 'interval' || order.intervalWeekday === null) return [];
  const start = fromIso > order.startDate ? fromIso : order.startDate;
  const end = toIso < order.endDate ? toIso : order.endDate;
  return intervalDates(start, end, order.intervalWeekday);
}

/**
 * Belegungsmuster: WANN belegt ein Auftrag eine Maschine wirklich?
 * Durchgehend (`standard`) oder nur an einem Wochentag im Zeitraum (`interval`).
 */
export interface BookingPattern {
  readonly kind: RentalKind;
  readonly weekday: number | null;
  readonly from: string;
  readonly to: string;
}

/** Der erste Tag ab `fromIso`, der auf `weekday` fällt (0=So … 6=Sa). */
export function nextWeekdayOnOrAfter(fromIso: string, weekday: number): string {
  const cursor = new Date(`${fromIso}T00:00:00.000Z`);
  cursor.setUTCDate(cursor.getUTCDate() + ((weekday - cursor.getUTCDay() + 7) % 7));
  return cursor.toISOString().slice(0, 10);
}

/**
 * Berühren sich zwei Belegungen an mindestens EINEM Tag?
 *
 * Vorher wurde nur die Art des BESTEHENDEN Auftrags berücksichtigt und die Anfrage immer als
 * durchgehender Zeitraum behandelt. Zwei Intervallmieten an verschiedenen Wochentagen galten damit
 * als Kollision, obwohl sie sich nie begegnen — die Maschinenauswahl sperrte zu viel.
 *
 * Ein `interval` OHNE Wochentag wird bewusst wie durchgehend behandelt: zu viel zu sperren ist
 * lästig, zu wenig ist eine Doppelvermietung. In der Datenbank ist dieser Zustand seit
 * 20260909090000 ohnehin per CHECK ausgeschlossen.
 */
export function patternsCollide(a: BookingPattern, b: BookingPattern): boolean {
  const from = a.from > b.from ? a.from : b.from;
  const to = a.to < b.to ? a.to : b.to;
  if (from > to) return false;
  const aDay = a.kind === 'interval' ? a.weekday : null;
  const bDay = b.kind === 'interval' ? b.weekday : null;
  if (aDay === null && bDay === null) return true;
  if (aDay !== null && bDay !== null && aDay !== bDay) return false;
  const day = aDay ?? bDay;
  return day === null ? true : nextWeekdayOnOrAfter(from, day) <= to;
}

/**
 * Der belegende Auftrag — oder `null`, wenn die Maschine frei ist.
 *
 * Gibt den AUFTRAG zurück, nicht nur ja/nein: die Meldung soll sagen, WER belegt. Ohne die Nummer
 * bleibt dem Anwender nur „ist belegt" und kein Weg, das aufzulösen.
 */
export function findBookingClash(
  orders: readonly RentalOrder[],
  machineNumber: string,
  pattern: BookingPattern,
  ignoreOrderId?: string,
): RentalOrder | null {
  for (const order of orders) {
    if (order.id === ignoreOrderId || !blockingStatuses.has(order.status)) continue;
    for (const item of order.items) {
      if (item.machineNumber !== machineNumber) continue;
      const belegt: BookingPattern = {
        kind: order.kind, weekday: order.intervalWeekday, from: item.startDate, to: item.endDate,
      };
      if (patternsCollide(pattern, belegt)) return order;
    }
  }
  return null;
}

/** Is `machineNumber` free for [from, to] across all orders (ignoring `ignoreOrderId`)? */
export function isMachineAvailable(
  orders: readonly RentalOrder[],
  machineNumber: string,
  fromIso: string,
  toIso: string,
  ignoreOrderId?: string,
  kind: RentalKind = 'standard',
  weekday: number | null = null,
): boolean {
  return findBookingClash(orders, machineNumber, { kind, weekday, from: fromIso, to: toIso }, ignoreOrderId) === null;
}

/** Die Meldung bei Doppelbelegung — wortgleich zur SQL-Fassung in `forq.rental_booking_clash`. */
export function bookingClashMessage(machineNumber: string, blockingNumber: string): string {
  return `Maschine ${machineNumber} ist im gewählten Zeitraum bereits vermietet (${blockingNumber}).`;
}
